import React, { useState, useEffect } from "react";
import { Modal, Button, Table, Input, FormGroup, Label, Pagination, PaginationItem, PaginationLink, Row, Col, Card, CardHeader, CardBody, Badge, InputGroup, InputGroupAddon } from "reactstrap";
import moment from "moment";
import api from "../../../../Api.js";
import { ClipLoader } from "react-spinners";

const UserLogs = ({ state, setState }) => {
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [logs, setLogs] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalLogs, setTotalLogs] = useState(0);
  const [search, setSearch] = useState("");
  const [dateFilter, setDateFilter] = useState("");
  const [methodFilter, setMethodFilter] = useState("");
  const [isSearching, setIsSearching] = useState(false);
  const [searchTimeout, setSearchTimeout] = useState(null);
  const [showOnlyCritical, setShowOnlyCritical] = useState(false);
  const [selectedLog, setSelectedLog] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [uniqueDates, setUniqueDates] = useState([]);
  const logsPerPage = 15;

  useEffect(() => {
    if (state.openUserLogs && state.userLogsId) {
      // Resetar todos os filtros quando o modal é aberto inicialmente
      setShowOnlyCritical(false);
      setSearch("");
      setDateFilter("");
      setMethodFilter("");
      setIsSearching(false);
      setPage(1);
      
      if (!isSearching) {
        loadLogs();
      }
    }
  }, [state.openUserLogs, state.userLogsId]);

  const loadLogs = async () => {
    if (!state.userLogsId) return;

    setLoading(true);
    try {
      // Aumentar o número de logs por página para um valor mais razoável
      const pageSize = 15;
      
      const response = await api.getUserLogs(
        `${state.userLogsId}?page=${page}&limit=${pageSize}&excludeProgress=true&includeDates=true`
      );

      if (response && response.data) {
        let newLogs = [];
        let total = 0;
        let availableDates = [];

        if (Array.isArray(response.data)) {
          newLogs = response.data;
          total = Math.max(page * pageSize, newLogs.length);
        } else if (response.data.logs) {
          newLogs = response.data.logs;
          if (response.data.pagination) {
            total = response.data.pagination.total;
          }
          // Usar datas disponíveis do backend se existirem
          if (response.data.availableDates) {
            availableDates = response.data.availableDates;
          }
        }

        setLogs(newLogs);
        setTotalLogs(total);
        setTotalPages(Math.ceil(total / pageSize));
        
        // Se temos datas disponíveis do backend, usamos elas
        if (availableDates && availableDates.length > 0) {
          setUniqueDates(availableDates);
        }
      }
    } catch (error) {
      console.error("Erro ao carregar logs:", error);
    } finally {
      setLoading(false);
    }
  };

  const loadAllLogs = async () => {
    if (!state.userLogsId) return;

    setSearchLoading(true);
    setIsSearching(true);
    
    try {
      // Usar parâmetro "all=true" para garantir que todos os logs sejam retornados
      const response = await api.getUserLogs(`${state.userLogsId}?all=true&limit=1000`);

      if (response && response.data) {
        let allLogs = [];
        
        if (Array.isArray(response.data)) {
          allLogs = response.data;
        } else if (response.data.logs) {
          allLogs = response.data.logs;
        }

        console.log("Total de logs carregados:", allLogs.length);
        setLogs(allLogs);
        setTotalLogs(allLogs.length);
        setPage(1);
      }
    } catch (error) {
      console.error("Erro ao carregar todos os logs:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  const loadAllCriticalLogs = async () => {
    if (!state.userLogsId) return;

    setSearchLoading(true);
    setIsSearching(true);
    
    try {
      // Carregar todos os logs com método PUT ou DELETE
      const response = await api.getUserLogs(`${state.userLogsId}?all=true&limit=1000&methods=PUT,DELETE`);

      if (response && response.data) {
        let allLogs = [];
        
        if (Array.isArray(response.data)) {
          allLogs = response.data;
        } else if (response.data.logs) {
          allLogs = response.data.logs;
        }

        console.log("Logs críticos carregados:", allLogs.length);
        setLogs(allLogs);
        setTotalLogs(allLogs.length);
        setPage(1);
      }
    } catch (error) {
      console.error("Erro ao carregar logs críticos:", error);
    } finally {
      setSearchLoading(false);
    }
  };

  // Modificar a função clearSearch para mostrar o indicador de carregamento
  const clearSearch = () => {
    setLoading(true);
    setSearch("");
    setDateFilter("");
    setMethodFilter("");
    setShowOnlyCritical(false);
    
    // Reseta a paginação
    setPage(1);
    
    // Define isSearching como false e carrega os dados padrão
    setTimeout(() => {
      setIsSearching(false);
      
      // Carregar logs normais e buscar as datas disponíveis
      Promise.all([
        loadLogs(),
        // Buscar todas as datas disponíveis (separado do carregamento normal)
        api.getUserLogs(`${state.userLogsId}?datesOnly=true&excludeProgress=true`)
          .then(response => {
            if (response.data && response.data.availableDates) {
              setUniqueDates(response.data.availableDates.sort().reverse());
            }
          })
          .catch(err => console.error("Erro ao carregar datas:", err))
      ])
      .finally(() => {
        setLoading(false);
      });
    }, 10);
  };

  const closeModal = () => {
    setState({ openUserLogs: false, userLogsId: null, userLogsName: "" });
    setShowOnlyCritical(false); // Adicionar esta linha para garantir reset do filtro
    clearSearch();
  };

  const handleSearch = () => {
    if (search || dateFilter || methodFilter) {
      loadAllLogs();
    }
  };

  const handleSearchChange = (e) => {
    setSearch(e.target.value);
    
    if (searchTimeout) {
      clearTimeout(searchTimeout);
    }
    
    if (!e.target.value && !dateFilter && !methodFilter) {
      setSearchTimeout(setTimeout(() => {
        clearSearch();
      }, 500));
    }
  };

  const handleDateFilterChange = (e) => {
    setDateFilter(e.target.value);
    if (e.target.value || search || methodFilter) {
      loadAllLogs();
    } else if (!e.target.value && !search && !methodFilter) {
      clearSearch();
    }
  };

  const handleMethodFilterChange = (e) => {
    setMethodFilter(e.target.value);
    if (e.target.value || search || dateFilter) {
      loadAllLogs();
    } else if (!e.target.value && !search && !dateFilter) {
      clearSearch();
    }
  };

  const handleShowOnlyCriticalChange = (e) => {
    setShowOnlyCritical(e.target.checked);
    
    // Se o filtro de críticos for ativado, carregamos TODOS os logs para mostrar todos os críticos
    if (e.target.checked) {
      loadAllCriticalLogs();
    } else {
      // Se desativado, voltamos para a visualização normal paginada
      loadLogs();
    }
  };

  const openLogDetails = (log) => {
    setSelectedLog(log);
    setShowDetailsModal(true);
  };

  const closeLogDetails = () => {
    setShowDetailsModal(false);
    setSelectedLog(null);
  };

  const getActionCategory = (log) => {
    const method = log.method || "";
    const url_min = log.url_min || "";

    if (method === 'GET') {
      return "Visualização";
    } else if (method === 'POST') {
      return "Criação";
    } else if (method === 'PUT') {
      return "Edição";
    } else if (method === 'DELETE') {
      return "Exclusão";
    } else {
      return "Outra";
    }
  };

  const getActionIcon = (category) => {
    switch (category) {
      case "Visualização": return "far fa-eye";
      case "Criação": return "fas fa-plus-circle";
      case "Edição": return "fas fa-edit";
      case "Exclusão": return "fas fa-trash";
      default: return "fas fa-cog";
    }
  };

  const getActionDescription = (log) => {
    const { method, url_min, status } = log;
    
    // Apenas retorna a descrição base sem a rota
    if (url_min.includes('/user/') && method === 'GET') {
      return "Visualizou informações de perfil";
    } else if (url_min.includes('/user/') && method === 'PUT') {
      return "Atualizou informações de cadastro";
    } else if (url_min.includes('/classroom/') && method === 'GET') {
      return "Acessou uma turma";
    } else if (url_min.includes('/progress/') && method === 'POST') {
      return "Registrou progresso em uma aula";
    } else if (url_min.includes('/task/') && method === 'GET') {
      return "Visualizou tarefas";
    } else if (url_min.includes('/task/') && method === 'POST') {
      return "Criou nova tarefa";
    } else if (url_min.includes('/task/') && method === 'PUT') {
      return "Atualizou uma tarefa";
    } else if (url_min.includes('/task-user-send/') && method === 'POST') {
      return "Enviou uma tarefa";
    } else if (url_min.includes('/chart/') && method === 'GET') {
      return "Visualizou relatórios estatísticos";
    } else {
      return method === 'GET' ? "Visualizou informações" :
             method === 'POST' ? "Criou um novo registro" :
             method === 'PUT' ? "Atualizou informações" :
             method === 'DELETE' ? "Removeu informações" : "Interagiu com o sistema";
    }
  };

  const actionCategories = ["Visualização", "Criação", "Edição", "Exclusão", "Outra"];

  const filteredLogs = logs.filter(log => {
    const category = getActionCategory(log);
    
    // Filtro para ações críticas (edição e exclusão)
    const matchesCritical = !showOnlyCritical || 
      category === "Edição" || 
      category === "Exclusão";

    const matchesSearch = search === "" || 
      (log.url && log.url.toLowerCase().includes(search.toLowerCase())) ||
      (log.url_min && log.url_min.toLowerCase().includes(search.toLowerCase())) ||
      (log.ip && log.ip.toLowerCase().includes(search.toLowerCase()));

    const matchesDate = dateFilter === "" || 
      moment(log.createDate).format('YYYY-MM-DD') === dateFilter;

    const matchesMethod = methodFilter === "" || 
      category === methodFilter;

    return matchesSearch && matchesDate && matchesMethod && matchesCritical;
  });

  const paginatedLogs = isSearching ? 
    filteredLogs.slice((page - 1) * logsPerPage, page * logsPerPage) :
    filteredLogs;

  const filteredTotalPages = Math.ceil(filteredLogs.length / logsPerPage);

  return (
    <Modal
      className="modal-dialog-centered modal-lg"
      isOpen={state.openUserLogs}
      toggle={closeModal}
    >
      <div className="modal-header">
        <h5 className="modal-title">
          Histórico de Atividades: {state.userLogsName}
        </h5>
        <button
          aria-label="Close"
          className="close"
          data-dismiss="modal"
          type="button"
          onClick={closeModal}
        >
          <span aria-hidden={true}>×</span>
        </button>
      </div>

      <div className="modal-body">
        {loading ? (
          <div className="text-center py-4">
            <ClipLoader size={40} color={"#5e72e4"} loading={loading} />
            <p className="mt-2">Carregando atividades...</p>
          </div>
        ) : (
          <>
            <Card className="shadow mb-4">
              <CardHeader className="border-0">
                <h6 className="mb-0">Filtros</h6>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col md="4">
                    <FormGroup>
                      <Label for="searchInput">Pesquisar</Label>
                      <InputGroup>
                        <Input
                          id="searchInput"
                          type="text"
                          placeholder="Digite para pesquisar..."
                          value={search}
                          onChange={handleSearchChange}
                          onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                        />
                        <InputGroupAddon addonType="append">
                          <Button color="primary" onClick={handleSearch}>
                            {searchLoading ? (
                              <ClipLoader size={15} color={"#ffffff"} loading={true} />
                            ) : (
                              <i className="fas fa-search"></i>
                            )}
                          </Button>
                        </InputGroupAddon>
                      </InputGroup>
                      {isSearching && (
                        <small className="text-success">
                          Pesquisando em todos os registros
                        </small>
                      )}
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="dateFilter">Filtrar por Data</Label>
                      <Input
                        id="dateFilter"
                        type="select"
                        value={dateFilter}
                        onChange={handleDateFilterChange}
                      >
                        <option value="">Todas as datas</option>
                        {uniqueDates.map(date => (
                          <option key={date} value={date}>
                            {moment(date).format('DD/MM/YYYY')}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="4">
                    <FormGroup>
                      <Label for="categoryFilter">Tipo de Atividade</Label>
                      <Input
                        id="categoryFilter"
                        type="select"
                        value={methodFilter}
                        onChange={handleMethodFilterChange}
                      >
                        <option value="">Todas as atividades</option>
                        {actionCategories.map(category => (
                          <option key={category} value={category}>
                            {category}
                          </option>
                        ))}
                      </Input>
                    </FormGroup>
                  </Col>
                  <Col md="12" className="mb-3">
                    <FormGroup check className="mt-2">
                      <Label check>
                        <Input
                          type="checkbox"
                          checked={showOnlyCritical}
                          onChange={handleShowOnlyCriticalChange}  // Use a nova função aqui
                        />
                        <span className="form-check-sign"></span>
                        <Badge color="warning" className="mr-1">Edição</Badge>
                        <Badge color="danger" className="mr-2">Exclusão</Badge>
                        Mostrar apenas atividades críticas (edição e exclusão)
                      </Label>
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {searchLoading ? (
              <div className="text-center py-5">
                <ClipLoader size={50} color={"#5e72e4"} loading={searchLoading} />
                <p className="mt-3 lead">Buscando em todos os registros...</p>
                <p className="text-muted">Isso pode levar alguns segundos</p>
              </div>
            ) : paginatedLogs.length > 0 ? (
              <div className="table-responsive">
                <Table className="align-items-center table-flush">
                  <thead className="thead-light">
                    <tr>
                      <th>Data e Hora</th>
                      <th>Tipo de Atividade</th>
                      <th>Descrição</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedLogs.map((log, index) => {
                      const category = getActionCategory(log);
                      const icon = getActionIcon(category);
                      const isCritical = category === "Edição" || category === "Exclusão";

                      return (
                        <tr 
                          key={`log-${index}`}
                          className={isCritical ? "table-warning" : ""} // Destacar linha para ações críticas
                        >
                          <td>{moment(log.createDate).format('DD/MM/YYYY HH:mm:ss')}</td>
                          <td>
                            <div className="d-flex align-items-center">
                              <i className={`${icon} mr-2`} style={{ width: '16px' }}></i>
                              <Badge
                                color={
                                  category === "Visualização" ? "info" :
                                  category === "Criação" ? "success" :
                                  category === "Edição" ? "warning" :
                                  category === "Exclusão" ? "danger" : "secondary"
                                }
                              >
                                {category}
                              </Badge>
                              {isCritical && (
                                <i className="fas fa-exclamation-triangle ml-2 text-warning"></i>
                              )}
                            </div>
                          </td>
                          <td>
                            <div 
                              onClick={() => openLogDetails(log)}
                              title="Clique para ver detalhes completos" 
                              style={{ 
                                maxWidth: "300px", 
                                overflow: "hidden", 
                                textOverflow: "ellipsis", 
                                whiteSpace: "nowrap",
                                fontWeight: isCritical ? "bold" : "normal",
                                cursor: "pointer",  // Adicione cursor pointer para indicar que é clicável
                                textDecoration: "underline dotted", // Sublinhado pontilhado para indicar informação
                                color: isCritical ? "#e74c3c" : "#3498db" // Cores diferentes para crítico vs. normal
                              }}
                            >
                              {getActionDescription(log)}
                            </div>
                          </td>
                          <td>
                            <Badge color={log.status >= 200 && log.status < 300 ? 'success' : 'danger'}>
                              {log.status >= 200 && log.status < 300 ? 'Sucesso' : 'Falha'}
                            </Badge>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>

                <div className="d-flex justify-content-between align-items-center mt-4">
                  <small className="text-muted">
                    Mostrando {paginatedLogs.length === 0 ? 0 : (page - 1) * logsPerPage + 1} a {Math.min(page * logsPerPage, isSearching ? filteredLogs.length : totalLogs)} de {isSearching ? filteredLogs.length : totalLogs} atividades
                  </small>

                  <Pagination>
                    <PaginationItem disabled={page === 1}>
                      <PaginationLink
                        onClick={() => setPage(page - 1)}
                        tabIndex="-1"
                      >
                        <i className="fas fa-angle-left" />
                        <span className="sr-only">Anterior</span>
                      </PaginationLink>
                    </PaginationItem>
                    {[...Array(isSearching ? filteredTotalPages : totalPages).keys()].map(index => {
                      const pageNumber = index + 1;
                      if (
                        pageNumber === 1 ||
                        pageNumber === (isSearching ? filteredTotalPages : totalPages) ||
                        (pageNumber >= page - 2 && pageNumber <= page + 2)
                      ) {
                        return (
                          <PaginationItem
                            key={pageNumber}
                            active={pageNumber === page}
                          >
                            <PaginationLink onClick={() => setPage(pageNumber)}>
                              {pageNumber}
                            </PaginationLink>
                          </PaginationItem>
                        );
                      } else if (
                        pageNumber === page - 3 ||
                        pageNumber === page + 3
                      ) {
                        return (
                          <PaginationItem key={pageNumber} disabled>
                            <PaginationLink>...</PaginationLink>
                          </PaginationItem>
                        );
                      }
                      return null;
                    })}
                    <PaginationItem disabled={page === (isSearching ? filteredTotalPages : totalPages)}>
                      <PaginationLink onClick={() => setPage(page + 1)}>
                        <i className="fas fa-angle-right" />
                        <span className="sr-only">Próxima</span>
                      </PaginationLink>
                    </PaginationItem>
                  </Pagination>
                </div>
              </div>
            ) : (
              <div className="text-center py-5">
                <p className="lead">Nenhuma atividade encontrada.</p>
                {isSearching && (
                  <Button color="primary" outline onClick={clearSearch} disabled={loading}>
                    {loading ? (
                      <>
                        <ClipLoader size={15} color={"#5e72e4"} loading={true} className="mr-2" />
                        Limpando...
                      </>
                    ) : (
                      "Limpar pesquisa"
                    )}
                  </Button>
                )}
              </div>
            )}
          </>
        )}
      </div>

      <div className="modal-footer">
        {isSearching && (
          <Button color="primary" outline onClick={clearSearch} disabled={loading}>
            {loading ? (
              <>
                <ClipLoader size={15} color={"#5e72e4"} loading={true} className="mr-2" />
                Limpando...
              </>
            ) : (
              "Limpar pesquisa"
            )}
          </Button>
        )}
        <Button color="secondary" onClick={closeModal}>
          Fechar
        </Button>
      </div>

      {/* Modal de detalhes do log - Versão redesenhada */}
      <Modal
        className="modal-dialog-centered"
        size="md"
        isOpen={showDetailsModal}
        toggle={closeLogDetails}
      >
        <div className="modal-header pb-0" style={{ borderBottom: 'none' }}>
          <button
            aria-label="Close"
            className="close"
            type="button"
            onClick={closeLogDetails}
          >
            <span aria-hidden={true}>×</span>
          </button>
        </div>
        <div className="modal-body pt-0">
          {selectedLog && (
            <div className="px-1">
              {/* Cabeçalho com informações principais */}
              <div className="text-center mb-4">
                <div 
                  className="icon icon-shape rounded-circle shadow mb-3"
                  style={{
                    backgroundColor: 
                      getActionCategory(selectedLog) === "Visualização" ? "#5bc0de" :
                      getActionCategory(selectedLog) === "Criação" ? "#5cb85c" :
                      getActionCategory(selectedLog) === "Edição" ? "#f0ad4e" :
                      getActionCategory(selectedLog) === "Exclusão" ? "#d9534f" : "#6c757d",
                    width: "60px", 
                    height: "60px",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center"
                  }}
                >
                  <i className={`${getActionIcon(selectedLog)} text-white`} style={{ fontSize: "1.5rem" }}></i>
                </div>
                <h4 className="mb-1">{getActionDescription(selectedLog)}</h4>
                <Badge
                  color={
                    getActionCategory(selectedLog) === "Visualização" ? "info" :
                    getActionCategory(selectedLog) === "Criação" ? "success" :
                    getActionCategory(selectedLog) === "Edição" ? "warning" :
                    getActionCategory(selectedLog) === "Exclusão" ? "danger" : "secondary"
                  }
                  pill
                  className="px-3 py-2"
                  style={{ fontSize: "0.85rem" }}
                >
                  {getActionCategory(selectedLog)}
                </Badge>
              </div>
              
              {/* Cards com informações detalhadas */}
              <Card className="shadow-sm border-0 mb-3">
                <CardBody className="py-3">
                  <Row>
                    <Col md="6">
                      <div className="mb-3">
                        <div className="d-flex align-items-center mb-2">
                          <i className="far fa-calendar-alt mr-2 text-primary"></i>
                          <h6 className="heading-small mb-0">Data e Hora</h6>
                        </div>
                        <p className="text-muted mb-0">{moment(selectedLog.createDate).format('DD/MM/YYYY HH:mm:ss')}</p>
                      </div>
                    </Col>
                    <Col md="6">
                      <div className="mb-3">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-code mr-2 text-primary"></i>
                          <h6 className="heading-small mb-0">Método</h6>
                        </div>
                        <Badge
                          color={
                            selectedLog.method === "GET" ? "info" :
                            selectedLog.method === "POST" ? "success" :
                            selectedLog.method === "PUT" ? "warning" :
                            selectedLog.method === "DELETE" ? "danger" : "secondary"
                          }
                          className="text-uppercase px-2"
                        >
                          {selectedLog.method}
                        </Badge>
                      </div>
                    </Col>
                  </Row>
                </CardBody>
              </Card>
              
              <Card className="shadow-sm border-0 mb-3">
                <CardBody className="py-3">
                <Row>
                    <Col md="6">
                      <div className="mb-3 mb-md-0">
                        <div className="d-flex align-items-center mb-2">
                          <i className="fas fa-check-circle mr-2 text-primary"></i>
                          <h6 className="heading-small mb-0">Status</h6>
                        </div>
                        <Badge 
                          color={selectedLog.status >= 200 && selectedLog.status < 300 ? 'success' : 'danger'}
                          pill
                          className="px-2"
                        >
                          {selectedLog.status} {selectedLog.status >= 200 && selectedLog.status < 300 ? '(Sucesso)' : '(Falha)'}
                        </Badge>
                      </div>
                    </Col>
                    <Col md="6">
                    <div className="mb-3">
                    <div className="d-flex align-items-center mb-2">
                      <i className="fas fa-link mr-2 text-primary"></i>
                      <h6 className="heading-small mb-0">Rota</h6>
                    </div>
                    <div className="bg-secondary p-3 rounded" style={{ wordBreak: "break-all", fontSize: "0.85rem" }}>
                      <code>{selectedLog.url}</code>
                    </div>
                  </div>
                    </Col>
                    
                  </Row>

                  
                </CardBody>
              </Card>
              
              <Card className="shadow-sm border-0 mb-3">
                <CardBody className="py-3">
                  
                </CardBody>
              </Card>
            </div>
          )}
        </div>
        <div className="modal-footer">
          <Button color="primary" onClick={closeLogDetails}>
            Fechar
          </Button>
        </div>
      </Modal>
    </Modal>
  );
};

export default UserLogs;